import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { AuthService } from '@/services/AuthService';
import { appConfig } from '@/config/app';
import { Loader2 } from 'lucide-react';

const authSvc = new AuthService();

/**
 * Magic link verification page.
 * Receives ?token=xxx from the email link, verifies it,
 * sets the session, and redirects to the dashboard.
 */
export function MagicLinkVerify() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = React.useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No token found in the URL.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const user = await authSvc.verifyMagicLink(token);
        if (cancelled) return;

        // Update auth store with the user
        useAuthStore.setState({ user, isLoading: false, error: null });

        setStatus('success');
        // Short delay so user sees the success state
        setTimeout(() => {
          if (!cancelled) navigate('/dashboard', { replace: true });
        }, 1000);
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Failed to verify magic link');
      }
    })();

    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <img
              src="/images/logo.png"
              alt={appConfig.name}
              className="h-8 mx-auto mb-4 object-contain"
            />
          </Link>
        </div>

        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Signing you in...</h2>
              <p className="text-sm text-muted-foreground">Verifying your magic link</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">You're in!</h2>
              <p className="text-sm text-muted-foreground">Redirecting to your dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Link expired or invalid</h2>
              <p className="text-sm text-muted-foreground mb-4">{errorMessage}</p>
              <Link
                to="/auth"
                className="inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
